import { UserButton } from '../auth';
import { useCurrentProfile } from '../shared/CurrentProfileProvider';

interface UserMenuProps {
  showEditProfile?: boolean;
  showFinancials?: boolean;
  showHostDashboard?: boolean;
  showRecordingReview?: boolean;
}

/**
 * Consistent user menu dropdown wrapping the auth adapter's UserButton.
 * Use this instead of bare UserButton to get standard menu items.
 */
export function UserMenu({
  showEditProfile = true,
  showFinancials = true,
  showHostDashboard = true,
  showRecordingReview = true,
}: UserMenuProps) {
  const { profile } = useCurrentProfile();
  const profilePhotoUrl = profile?.photos?.[0]?.url ?? null;

  return (
    <div className="relative inline-flex h-8 w-8">
      {profilePhotoUrl && (
        <img
          src={profilePhotoUrl}
          alt=""
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 z-10 h-8 w-8 rounded-full object-cover"
        />
      )}
      <UserButton
        appearance={{
          elements: {
            avatarBox: `w-8 h-8 ${profilePhotoUrl ? 'opacity-0' : ''}`,
          },
        }}
      >
        <UserButton.MenuItems>
          {showEditProfile && (
            <UserButton.Link
              label="Edit Profile"
              labelIcon={
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
              }
              href="/profile"
            />
          )}
          {showFinancials && (
            <UserButton.Link
              label="Financials"
              labelIcon={
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 19V5" />
                  <path d="M4 19h16" />
                  <path d="M8 16V9" />
                  <path d="M12 16V7" />
                  <path d="M16 16v-4" />
                </svg>
              }
              href="/financials"
            />
          )}
          {showHostDashboard && (
            <UserButton.Link
              label="Dashboard"
              labelIcon={
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="4" width="18" height="18" rx="2" />
                  <path d="M16 2v4M8 2v4M3 10h18" />
                </svg>
              }
              href="/dashboard"
            />
          )}
          {showRecordingReview && (
            <UserButton.Link
              label="Review Recordings"
              labelIcon={
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M23 7l-7 5 7 5V7z" />
                  <rect x="1" y="5" width="15" height="14" rx="2" />
                </svg>
              }
              href="/recordings/review"
            />
          )}
        </UserButton.MenuItems>
      </UserButton>
    </div>
  );
}
